import { libelleMetier } from '../../config/servicesCatalog';
import { estOuvertMaintenant, formaterEtatOuverture } from '../../utils/horaires';

/**
 * File d'appel (§5.4) — la liste triée par score, avec le badge d'ouverture **recalculé à chaque
 * rendu** (`estOuvertMaintenant`, jamais stocké).
 *
 * Pas de bouton « Appeler » (le `tel:` de bureau ouvrait Skype/Teams) ni « Itinéraire » (aucune
 * visite) : le numéro est affiché en clair et sélectionnable, le clic sur le nom ouvre la fiche.
 */

const TONS_BADGES = {
  succes: 'badge-success',
  echec: 'badge-error',
  neutre: 'badge-ghost'
};

const BadgeOuverture = ({ openingHours }) => {
  const etat = estOuvertMaintenant(openingHours);
  const badge = formaterEtatOuverture(etat);

  return (
    <span className={`badge badge-sm ${TONS_BADGES[badge.ton] || 'badge-ghost'} whitespace-nowrap`}>
      {badge.icone} {badge.texte}
    </span>
  );
};

const AppelDuJourList = ({ prospects = [], onOuvrir }) => {
  if (prospects.length === 0) {
    return (
      <div className="text-center py-12 text-base-content/60">
        <p className="text-lg mb-2">Aucun prospect à appeler</p>
        <p className="text-sm">Commencez par importer un fichier Apify, ou vérifiez vos filtres.</p>
      </div>
    );
  }

  return (
    <ul className="space-y-2">
      {prospects.map((prospect) => {
        const avis = prospect.google?.reviewCount;
        const note = prospect.google?.rating;
        const nonRevendiquee = prospect.google?.claimed === false;

        return (
          <li
            key={prospect.placeId}
            className="border border-base-200 rounded-lg p-3 hover:bg-base-200/40 transition-colors"
          >
            <div className="flex items-start gap-4">
              {/* Le score est le seul chiffre qui ordonne */}
              <div className="text-2xl font-bold text-primary w-12 text-center shrink-0">
                {prospect.score ?? '—'}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <button
                    type="button"
                    className="font-semibold text-left hover:underline truncate"
                    onClick={() => onOuvrir(prospect.placeId)}
                  >
                    {prospect.name}
                  </button>
                  <BadgeOuverture openingHours={prospect.openingHours} />
                </div>

                <div className="text-sm text-base-content/70 mt-1 flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span>
                    {prospect.primaryCategory || libelleMetier(prospect.targetTrade) || 'Métier inconnu'}
                  </span>
                  {prospect.address?.city && (
                    <span>
                      · {prospect.address.city}
                      {prospect.departmentCode ? ` (${prospect.departmentCode})` : ''}
                    </span>
                  )}
                  {typeof note === 'number' && (
                    <span>
                      · ⭐ {String(note).replace('.', ',')}
                      {typeof avis === 'number' ? ` (${avis} avis)` : ''}
                    </span>
                  )}
                  {nonRevendiquee && (
                    <span className="badge badge-sm badge-warning">🚫 non revendiquée</span>
                  )}
                  {!prospect.address?.isComplete && (
                    <span className="badge badge-sm badge-error">⚠️ Adresse manquante</span>
                  )}
                  {prospect.callAttempts >= 3 && (
                    <span className="badge badge-sm badge-error">⚠️ 3 tentatives épuisées</span>
                  )}
                </div>
                {/* ACTIONS — le numéro en clair remplace le bouton « Appeler » (D7) */}
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <span className="text-sm font-mono font-semibold select-all">
                    {prospect.phone || 'Sans numéro'}
                  </span>

                  {prospect.google?.mapsUrl && (
                    <a
                      href={prospect.google.mapsUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-sm btn-outline"
                    >
                      🗺️ Fiche
                    </a>
                  )}

                  {/* Étape 2 : présent dans la mise en page, volontairement inactif */}
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost ml-auto"
                    disabled
                    title="Disponible à l’étape 2"
                  >
                    ➕ Enregistrer la demande
                  </button>
                </div>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default AppelDuJourList;
